import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { APP_NAME } from "@/config";
import { useFlags } from "@/lib/flags";
import { Page, PageHeader, StatusBadge, Loading, int, plural, ago, usd, resources } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import { cn } from "@/lib/utils";
import { Plus } from "lucide-react";

// design_handoff_crewbench/screens/Runners.dc.html: each company's workspace server as a card, adding one (a one-time
// install command), its session limit, rebuilding and retiring (both with the admin's password). With FEATURE_DOCUMENTS,
// Sidelab's document server too (the founder's assistant, backend/doc_jobs.py): a runner of no company that prepares
// every company's documents, each in its own container.
const KIND = { standard_vm: "Standard cloud server", cloud_vm: "Cloud server", dedicated: "Dedicated server", local_demo: "Local demo server" };
const TONE = { online: "info", rebuilding: "warning", waiting: "warning", offline: "danger", retired: "muted" };
const pctOf = (n) => (n == null ? "—" : `${Math.round(n)}%`);

function Chip({ on, onClick, children }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={cn("inline-flex items-center h-8 px-3 rounded-md font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        on ? "bg-gr-selected text-gr-on-selected" : "border border-gr-control-border hover:bg-gr-hover")}>{children}</button>
  );
}

function AddRunner({ open, onOpenChange, companies, onCommand }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ company_id: "", name: "", kind: "cloud_vm", monthly_cost: "0" });
  useEffect(() => { if (open) setF({ company_id: "", name: "", kind: "cloud_vm", monthly_cost: "0" }); }, [open]);
  const add = useMutation({
    mutationFn: async () => (await api.post("/admin/runners", { ...f, name: f.name.trim(), monthly_cost: Number(f.monthly_cost) || 0 })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["admin-runners"] });
      onOpenChange(false);
      onCommand({ command: d.install_command, company: companies.find((c) => c.id === f.company_id)?.name });
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const ready = f.company_id && f.name.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-3.5">
        <DialogTitle>Add a runner</DialogTitle>
        <DialogDescription className="text-gr-ink-secondary [text-wrap:pretty]">Pick the company. You get a one-time registration token and the command to install the runner with it.</DialogDescription>
        <div role="radiogroup" aria-label="Company" className="flex flex-wrap gap-2" data-testid="runner-company">
          {companies.filter((c) => !c.deleted_at).map((c) => (
            <Chip key={c.id} on={f.company_id === c.id} onClick={() => setF({ ...f, company_id: c.id, name: f.name || `${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-runner` })}>{c.name}</Chip>
          ))}
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex-[2_1_200px] flex flex-col gap-1.5"><Label htmlFor="runner-name">Name</Label>
            <Input id="runner-name" className="font-mono text-[12px]" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="runner-name" /></div>
          <div className="flex-[1_1_140px] flex flex-col gap-1.5"><Label htmlFor="runner-cost">Monthly cost (USD)</Label>
            <Input id="runner-cost" type="number" min="0" className="tabular-nums" value={f.monthly_cost} onChange={(e) => setF({ ...f, monthly_cost: e.target.value })} data-testid="runner-cost" /></div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Kind</span>
          <div className="flex flex-wrap gap-2" data-testid="runner-kind">
            {[["cloud_vm", "Cloud server"], ["dedicated", "Dedicated server"]].map(([k, l]) => <Chip key={k} on={f.kind === k} onClick={() => setF({ ...f, kind: k })}>{l}</Chip>)}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button aria-disabled={!ready} disabled={add.isPending} onClick={() => ready && add.mutate()} data-testid="runner-create-btn">Create token</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Limit({ runner, company, onClose }) {
  const qc = useQueryClient();
  const [v, setV] = useState("");
  useEffect(() => { if (runner) setV(runner.max_active_sessions ? String(runner.max_active_sessions) : ""); }, [runner]);
  const save = useMutation({
    mutationFn: async (max) => api.put(`/admin/runners/${runner.id}/limit`, { max_active_sessions: max }),
    onSuccess: (_r, max) => { qc.invalidateQueries({ queryKey: ["admin-runners"] }); onClose(); toast.success(max ? `Session limit saved: ${max} at once.` : "Session limit removed."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Dialog open={!!runner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="gap-3.5">
        <DialogTitle>Session limit for {runner?.name}</DialogTitle>
        <DialogDescription className="text-gr-ink-secondary">How many of {company || "its company"}'s sessions can be active at once on this server. Empty: no limit.</DialogDescription>
        <label className="flex items-center gap-2 self-start h-8 px-2.5 rounded-md border border-gr-control-border focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
          <input type="number" min="1" step="1" aria-label="Sessions at once" value={v} onChange={(e) => setV(e.target.value)}
            className="w-14 border-0 bg-transparent text-[13px] outline-none tabular-nums" />
          <span className="text-muted-foreground">sessions at once</span>
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending} onClick={() => save.mutate(v.trim() === "" ? null : Math.max(1, Number(v) || 1))}>Save limit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InstallCommand({ shown, onClose }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(shown.command); toast.success("Install command copied."); }
    catch { toast.error("Copy failed. Select the command and copy it."); }
  };
  return (
    <Dialog open={!!shown} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="gap-3.5">
        <DialogTitle>Install command</DialogTitle>
        <DialogDescription className="text-gr-ink-secondary">For {shown?.company || "the company"}.</DialogDescription>
        <div className="flex flex-col gap-2">
          <div className="px-3 py-2.5 rounded-lg border bg-background font-mono text-[12px] leading-[18px] [overflow-wrap:anywhere]" data-testid="install-command">{shown?.command}</div>
          <span className="text-sm text-muted-foreground">Run it on the new server. The token works once; the runner swaps it for its own and shows online within a minute.</span>
          <span className="text-sm text-muted-foreground">While the runner image is private, run <span className="font-mono">docker login ghcr.io</span> first with a read:packages token, or use runner/install.sh with GHCR_USER and GHCR_TOKEN.</span>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={copy}>Copy command</Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// backend/alerts.py: the backend's warnings and errors are emailed to ADMIN_EMAIL; a test one shows they arrive
function Alerts() {
  const { data } = useQuery({ queryKey: ["admin-alerts"], queryFn: async () => (await api.get("/admin/alerts")).data });
  const test = useMutation({
    mutationFn: async () => (await api.post("/admin/alerts/test")).data,
    onSuccess: (d) => toast.success(`Test alert logged. Its email goes to ${d.to} within a minute.`),
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return null;
  const off = !data.email || !data.to;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-card rounded-[10px] px-[18px] py-3 text-sm" data-testid="admin-alerts">
      <span className="text-muted-foreground [text-wrap:pretty]">{off
        ? "Backend alerts are off: they need email on and ADMIN_EMAIL set on the backend."
        : `Backend warnings and errors are emailed to ${data.to}, at most every ${data.every_minutes} minutes.`}</span>
      <Button variant="outline" disabled={off || test.isPending} onClick={() => test.mutate()} data-testid="alerts-test">Send a test alert</Button>
    </div>
  );
}

export default function Runners() {
  const qc = useQueryClient();
  const flags = useFlags();
  const [adding, setAdding] = useState(false);
  const [command, setCommand] = useState(null);  // {command, company}
  const [limiting, setLimiting] = useState(null);
  const [asking, setAsking] = useState(null);  // {kind: "rebuild" | "retire", runner}: confirmed with the password
  const { data, isLoading } = useQuery({ queryKey: ["admin-runners"], queryFn: async () => (await api.get("/admin/runners")).data, refetchInterval: 5000 });
  const { data: companies } = useQuery({ queryKey: ["admin-companies"], queryFn: async () => (await api.get("/admin/companies")).data });
  const names = Object.fromEntries((companies?.companies || []).map((c) => [c.id, c.name]));
  const rebuild = useMutation({
    mutationFn: async ({ runner, password }) => (await api.post(`/admin/runners/${runner.id}/rebuild`, { password })).data,
    onSuccess: (d, { runner }) => {
      setAsking(null); qc.invalidateQueries({ queryKey: ["admin-runners"] });
      setCommand({ command: d.install_command, company: names[runner.company_id] });
      toast.success(`${runner.name} is rebuilding. ${names[runner.company_id] || "Its company"}'s sessions are locked.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const retire = useMutation({
    mutationFn: async ({ runner, password }) => api.post(`/admin/runners/${runner.id}/retire`, { password }),
    onSuccess: (_r, { runner }) => { setAsking(null); qc.invalidateQueries({ queryKey: ["admin-runners"] }); toast.success(`${runner.name} retired.`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const addDocs = useMutation({
    mutationFn: async () => (await api.post("/admin/document-servers", {})).data,
    onSuccess: (d) => setCommand({ command: d.install_command, company: "the document server" }),
    onError: (e) => toast.error(apiErr(e)),
  });
  const action = (<>
    {flags.documents && <Button variant="outline" onClick={() => addDocs.mutate()} disabled={addDocs.isPending}
      data-testid="add-document-server-btn"><Plus />Add document server</Button>}
    <Button onClick={() => setAdding(true)} data-testid="add-runner-btn"><Plus />Add runner</Button>
  </>);
  const header = (status) => <PageHeader eyebrow="Platform · Runners" title="One isolated workspace server per company." status={status} actions={action} />;
  if (isLoading || !data) return <Page className="gap-5">{header()}<Loading /></Page>;

  const order = { offline: 0, rebuilding: 1, waiting: 1, online: 2, retired: 3 };
  const runners = [...data.runners].sort((a, b) => (order[a.status] ?? 2) - (order[b.status] ?? 2));
  const live = runners.filter((r) => r.status !== "retired");
  const offline = live.filter((r) => r.status === "offline").length;
  const company = (r) => (r.role === "documents" ? "Document server, every company's documents" : names[r.company_id] || "A deleted company");
  return (
    <Page className="gap-5" data-testid="admin-runners">
      {header(offline ? `${plural(offline, "runner")} ${offline === 1 ? "is" : "are"} offline.` : live.length ? "Every runner is online." : "No runners yet.")}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
        {runners.map((r) => {
          const stats = [["Heartbeat", ago(r.last_heartbeat_at)],
                         ["Sessions", r.max_active_sessions ? `${int(r.active_sessions)} of ${int(r.max_active_sessions)}` : int(r.active_sessions)],
                         ["CPU", pctOf(r.cpu)], ["Memory", pctOf(r.memory)], ["Disk", pctOf(r.disk)], ["Version", r.version || "—"],
                         ["Machine", r.machine ? resources(r.machine) : "—"], ["Each session", resources(r.session_resources)],
                         ["Cost", r.monthly_cost ? `${usd(r.monthly_cost, 0)}/mo` : "—"]];
          return (
            <article key={r.id} className="bg-card rounded-[10px] p-[18px] flex flex-col gap-3 tabular-nums" data-testid={`runner-row-${r.name}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex flex-col min-w-0">
                  <span className="font-mono text-[13px] font-medium [overflow-wrap:anywhere]">{r.name}</span>
                  <span className="text-sm text-muted-foreground">{company(r)} · {KIND[r.kind] || r.kind}</span>
                </div>
                <StatusBadge status={r.status} tone={TONE[r.status]} />
              </div>
              {r.status === "offline" && (
                <span className="text-sm text-gr-danger [text-wrap:pretty]">Silent since {ago(r.last_heartbeat_at).toLowerCase()}. {r.role === "documents"
                  ? "Documents can't be prepared until it's back."
                  : `New sessions for ${company(r)} fail with "Your workspace server is offline".`}</span>
              )}
              <div className="flex flex-wrap gap-px bg-gr-hairline border rounded-lg overflow-hidden text-sm">
                {stats.map(([label, value]) => (
                  <div key={label} className="flex-[1_1_90px] bg-card px-2.5 py-2 flex flex-col">
                    <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">{label}</span><span>{value}</span>
                  </div>
                ))}
              </div>
              {r.status !== "retired" && (
                <div className="flex flex-wrap gap-2">
                  {(flags.provisioning || flags.store) && r.role !== "documents" && <Button variant="outline" onClick={() => setLimiting(r)} data-testid={`runner-limit-${r.name}`}>Session limit</Button>}
                  <Button variant="outline" onClick={() => setAsking({ kind: "rebuild", runner: r })} data-testid={`runner-rebuild-${r.name}`}>Rebuild</Button>
                  <Button variant="danger" onClick={() => setAsking({ kind: "retire", runner: r })} data-testid={`runner-retire-${r.name}`}>Retire</Button>
                </div>
              )}
            </article>
          );
        })}
      </div>
      <Alerts />
      <p className="m-0 text-sm text-muted-foreground [text-wrap:pretty]">
        Runners connect out to {APP_NAME} and expose no inbound ports. Only platform admins reach them, over the allowlisted addresses. A company's sessions never run on another company's runner.
      </p>
      <AddRunner open={adding} onOpenChange={setAdding} companies={companies?.companies || []} onCommand={setCommand} />
      <Limit runner={limiting} company={limiting && names[limiting.company_id]} onClose={() => setLimiting(null)} />
      <InstallCommand shown={command} onClose={() => setCommand(null)} />
      <ConfirmDestructive open={!!asking} onOpenChange={(v) => !v && setAsking(null)}
        title={asking?.kind === "retire" ? `Retire ${asking?.runner.name}?` : `Rebuild ${asking?.runner.name}?`}
        action={asking?.kind === "retire" ? "Retire" : "Rebuild"} pending={rebuild.isPending || retire.isPending}
        onConfirm={(password) => (asking.kind === "retire" ? retire : rebuild).mutate({ runner: asking.runner, password })}>
        <p className="m-0 [text-wrap:pretty]">{asking?.kind === "retire"
          ? `${asking && company(asking.runner)}'s sessions can't run until a new runner registers. Its token is revoked. The server itself isn't deleted.`
          : `This locks ${asking && company(asking.runner)}'s sessions, revokes the runner's token and gives you a new install command. Sessions restore from git and their backups on their next unlock, with no commits lost.`}</p>
      </ConfirmDestructive>
    </Page>
  );
}
